import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { actionErrorMessage } from "../../utils/network";
import type { TodaysAppointmentOut } from "../../services/dashboard.service";
import { AppointmentAPI } from "../../services/appointment.service";
import { RescheduleAppointmentModal } from "../appointments/RescheduleAppointmentModal";
import SectionCard from "./SectionCard";
import { useRole } from "../../hooks/useRole";

const STATUS_STYLE: Record<string, string> = {
  scheduled:   "bg-blue-100 text-blue-700",
  confirmed:   "bg-green-100 text-green-700",
  upcoming:    "bg-blue-100 text-blue-700",
  in_progress: "bg-amber-100 text-amber-700",
  completed:   "bg-green-100 text-green-700",
  cancelled:   "bg-red-100 text-red-700",
  no_show:     "bg-slate-200 text-slate-600",
};

// Doctor can act on these statuses
const ACTIONABLE = new Set(["scheduled", "upcoming", "confirmed"]);
// Confirm only makes sense before the doctor has confirmed
const NEEDS_CONFIRM = new Set(["scheduled", "upcoming"]);

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
}

function statusLabel(s: string): string {
  return s.replace("_", " ");
}

function doctorLabel(name: string): string {
  return /^dr\.?\s/i.test(name.trim()) ? name : `Dr. ${name}`;
}

function notesLabel(notes: string | null): string | null {
  if (!notes) return null;
  const first = notes.split(/[\n.]/)[0]?.trim();
  if (!first) return null;
  return first.length > 24 ? `${first.slice(0, 24)}…` : first;
}

interface Props {
  items: TodaysAppointmentOut[];
  onActionComplete?: () => void;
}

export const TodaysAppointments: React.FC<Props> = ({ items, onActionComplete }) => {
  const navigate = useNavigate();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // "View" opens /appointments (appointments.view); Confirm / Reschedule /
  // Cancel change the appointment (appointments.manage). The dashboard itself
  // needs neither, so each item follows its own grant.
  const { canOr } = useRole();
  const canViewAppointments = canOr("appointments.view", true);
  const canManageAppointments = canOr("appointments.manage", true);
  const [statusOverrides, setStatusOverrides] = useState<Record<string, string>>({});

  // Clear local overrides whenever fresh server data arrives so stale optimistic
  // state never permanently masks the real status from the refetch.
  useEffect(() => {
    setStatusOverrides({});
  }, [items]);

  const [rescheduleTarget, setRescheduleTarget] = useState<{
    appointmentId: string;
    doctorId: string;
    startTime: string;
  } | null>(null);

  // Close menu on any outside click — data-menu-id identifies the open menu's DOM subtree
  useEffect(() => {
    if (!openMenuId) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(`[data-menu-id="${openMenuId}"]`)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openMenuId]);

  const handleConfirm = async (id: string) => {
    setOpenMenuId(null);
    try {
      await AppointmentAPI.confirm(id);
      setStatusOverrides((prev) => ({ ...prev, [id]: "confirmed" }));
      toast.success("Appointment confirmed.");
      onActionComplete?.();
    } catch (err) {
      toast.error(actionErrorMessage(err, "Failed to confirm appointment. Please try again."));
    }
  };

  const handleCancel = async (id: string) => {
    setOpenMenuId(null);
    const reason = window.prompt("Reason for cancellation (required):");
    if (!reason?.trim()) return;
    try {
      await AppointmentAPI.cancel(id, reason.trim());
      setStatusOverrides((prev) => ({ ...prev, [id]: "cancelled" }));
      toast.success("Appointment cancelled.");
      onActionComplete?.();
    } catch (err) {
      toast.error(actionErrorMessage(err, "Failed to cancel appointment. Please try again."));
    }
  };

  return (
    <>
      <SectionCard
        title="Today's Appointments"
        action={
          <button
            onClick={() => navigate("/appointments")}
            className="text-xs font-medium text-slate-500 hover:text-slate-800 flex items-center gap-1"
          >
            View All <span aria-hidden>›</span>
          </button>
        }
      >
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">No appointments scheduled for today.</p>
        ) : (
          <ul className="space-y-2">
            {items.map((it) => {
              const effectiveStatus = statusOverrides[it.appointment_id] ?? it.status;
              const statusKey = effectiveStatus.toLowerCase();
              const noteText = notesLabel(it.notes);
              const isActionable =
                ACTIONABLE.has(statusKey) && (canViewAppointments || canManageAppointments);
              const canConfirm = canManageAppointments && NEEDS_CONFIRM.has(statusKey);
              const isMenuOpen = openMenuId === it.appointment_id;

              return (
                <li
                  key={it.appointment_id}
                  className="flex items-center gap-3 rounded-xl border border-slate-100 px-3 py-2.5 hover:bg-slate-50 transition-colors"
                >
                  <div className="h-9 w-9 rounded-full bg-slate-200 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900 truncate">
                        {it.patient_name}
                      </span>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${STATUS_STYLE[statusKey] ?? "bg-slate-100 text-slate-600"}`}>
                        {statusLabel(statusKey)}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                          <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 12 0V4a2 2 0 0 0-2-2h-1"/>
                          <path d="M8 15v1a6 6 0 0 0 12 0v-4"/><circle cx="20" cy="10" r="2"/>
                        </svg>
                        {doctorLabel(it.doctor_name)}
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                        </svg>
                        {fmtTime(it.start_time)}
                      </span>
                      {noteText && (
                        <span className="flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <path d="M14 2v6h6"/>
                          </svg>
                          {noteText}
                        </span>
                      )}
                    </div>
                  </div>

                  {isActionable && (
                    <div
                      className="relative flex-shrink-0"
                      data-menu-id={it.appointment_id}
                    >
                      <button
                        onClick={() => setOpenMenuId(isMenuOpen ? null : it.appointment_id)}
                        className="text-slate-400 hover:text-slate-600 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
                        aria-label="More options"
                      >
                        ⋯
                      </button>

                      {isMenuOpen && (
                        <div className="absolute right-0 top-full mt-1 z-50 w-44 bg-white border border-slate-200 rounded-xl shadow-lg py-1 text-sm">
                          {canViewAppointments && (
                            <button
                              onClick={() => { setOpenMenuId(null); navigate("/appointments"); }}
                              className="w-full text-left px-4 py-2 text-slate-700 hover:bg-slate-50 transition-colors"
                            >
                              View
                            </button>
                          )}
                          {canConfirm && (
                            <button
                              onClick={() => handleConfirm(it.appointment_id)}
                              className="w-full text-left px-4 py-2 text-green-600 font-medium hover:bg-green-50 transition-colors"
                            >
                              Confirm
                            </button>
                          )}
                          {canManageAppointments && (
                            <>
                              <button
                                onClick={() => {
                                  setOpenMenuId(null);
                                  setRescheduleTarget({
                                    appointmentId: it.appointment_id,
                                    doctorId: it.doctor_id,
                                    startTime: it.start_time,
                                  });
                                }}
                                className="w-full text-left px-4 py-2 text-slate-700 hover:bg-slate-50 transition-colors"
                              >
                                Reschedule
                              </button>
                              <button
                                onClick={() => handleCancel(it.appointment_id)}
                                className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 transition-colors"
                              >
                                Cancel
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>

      {rescheduleTarget && (
        <RescheduleAppointmentModal
          isOpen
          appointmentId={rescheduleTarget.appointmentId}
          doctorId={rescheduleTarget.doctorId}
          currentSlotStart={rescheduleTarget.startTime}
          onClose={() => setRescheduleTarget(null)}
          onSuccess={() => {
            setRescheduleTarget(null);
            setStatusOverrides((prev) => ({ ...prev, [rescheduleTarget.appointmentId]: "cancelled" }));
            toast.success("Appointment rescheduled.");
            onActionComplete?.();
          }}
        />
      )}
    </>
  );
};

export default TodaysAppointments;
